import type { ComponentProps, ReactNode } from 'react';

import { cn } from '@playbook/utils';

import css from './styles.module.css';

/**
 *	Figma's cell `type` has 25 values; the ones below are drawn by the cell
 *	itself. `code` is Figma's `group code`, a medium-weight figure. `control`
 *	stands for Figma's `icon`, `menu`, `expandable`, `button` and `button
 *	group`, which are all a control in a column that hugs it. Every other Figma
 *	type is an existing entity in `content`, or a cell of its own under
 *	`compositions/table-*-cell`.
 */
export type TableCellType =
	| 'text'
	| 'text-emphasized'
	| 'number'
	| 'code'
	| 'lead'
	| 'control';

const DefaultTableCellType: TableCellType = 'text' as const;

/**
 *	The same set as `TableRowState`. Figma's `hover` is CSS.
 */
export type TableCellState =
	| 'enabled'
	| 'selected'
	| 'disabled'
	| 'warning'
	| 'success'
	| 'error';

export type TableCellAlign = 'left' | 'right';

type TableCellEventHandlers = {
	[K in keyof ComponentProps<'td'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'td'>[K];
};

interface TableCellDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface TableCellProperties
	extends TableCellEventHandlers, TableCellDataAttributes {
	type?: TableCellType;
	/**
	 *	Paints this one cell in a state, over the row's. Left out, the cell
	 *	reads the row's look.
	 */
	state?: TableCellState;
	/**
	 *	Defaults to `right` for `number` and `code`, so digits line up, and
	 *	`left` otherwise.
	 */
	align?: TableCellAlign;

	label?: string;
	/**
	 *	A second, quieter line: stacked under `label` in a default table and
	 *	beside it in a small one, as Figma's `user extended` cell draws it.
	 */
	detail?: string;

	/** Drawn before the label, such as an avatar or a crest. */
	leading?: ReactNode;
	/** Drawn after the label, such as an arrow or a copy button. */
	trailing?: ReactNode;
	/** Any entity to show in the cell. Rendered instead of `label`. */
	content?: ReactNode;
}

export function TableCell({
	type = DefaultTableCellType,
	state,
	align = type === 'number' || type === 'code' ? 'right' : 'left',

	label,
	detail,

	leading,
	trailing,
	content,

	...props
}: TableCellProperties): ReactNode {
	const className: string = cn(
		css.cell,
		css[type],
		state && css[state],
		css[align]
	);

	return (
		<td
			{...props}
			data-type={type}
			data-state={state}
			data-align={align}
			className={className}>
			<div className={css.inner}>
				{leading}
				{content ?? (
					<span className={css.lines}>
						{label && <span className={css.label}>{label}</span>}
						{detail && <span className={css.detail}>{detail}</span>}
					</span>
				)}
				{trailing && <span className={css.trailing}>{trailing}</span>}
			</div>
		</td>
	);
}
